<template>
    <div class="table-permissions-page">
        <div class="page-header">
            <div class="header-left">
                <SupsisButton ghost @click="goBack">
                    <i class="material-icons">arrow_back</i>
                    {{ $t('common.back') }}
                </SupsisButton>
                <div class="title-section">
                    <h2 class="page-title">
                        <i class="material-icons">lock</i>
                        {{ $t('tables.permissions.title') }}
                    </h2>
                    <p class="page-subtitle" v-if="table">{{ table.name }}</p>
                </div>
            </div>
            <div class="header-right">
                <SupsisButton @click="onCancel">
                    {{ $t('common.cancel') }}
                </SupsisButton>
                <SupsisButton primary @click="onSave" :loading="saving">
                    {{ $t('common.save') }}
                </SupsisButton>
            </div>
        </div>

        <div class="page-content" v-if="table">
            <div class="mode-switch-section">
                <div class="mode-label">
                    <i class="material-icons">{{ permissionMode === 'public' ? 'public' : 'lock' }}</i>
                    <span class="mode-title">{{ $t('tables.permissions.mode') }}</span>
                </div>
                <div class="mode-switch">
                    <a-switch 
                        v-model="isCustomMode" 
                        @change="onModeChange"
                        :disabled="saving"
                    >
                        <span slot="checkedChildren">{{ $t('tables.permissions.customMode') }}</span>
                        <span slot="unCheckedChildren">{{ $t('tables.permissions.publicMode') }}</span>
                    </a-switch>
                </div>
                <div class="mode-description">
                    <span v-if="permissionMode === 'public'">
                        {{ $t('tables.permissions.publicModeDesc') }}
                    </span>
                    <span v-else>
                        {{ $t('tables.permissions.customModeDesc') }}
                    </span>
                </div>
            </div>

            <div v-if="permissionMode === 'custom'" class="custom-permissions">
                <a-tabs v-model="activeTab" type="card">
                    <a-tab-pane key="users" tab="Kullanıcılar">
                        <UserPermissionsTab
                            :users="users"
                            :departments="departments"
                            :permission-targets="permissionTargets"
                            :scope-groups="scopeGroups"
                            :current-user-id="currentUserId"
                            @toggle-scope="onToggleScope"
                            @activate-target="onActivateTarget"
                        />
                    </a-tab-pane>
                    <a-tab-pane key="departments" tab="Departmanlar">
                        <DepartmentPermissionsTab
                            :departments="departments"
                            :users="users"
                            :permission-targets="permissionTargets"
                            :scope-groups="scopeGroups"
                            @toggle-scope="onToggleScope"
                            @activate-target="onActivateTarget"
                        />
                    </a-tab-pane>
                </a-tabs>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import SupsisButton from "../Settings/components/SupsisButton.vue";
import UserPermissionsTab from "./components/permissions/UserPermissionsTab.vue";
import DepartmentPermissionsTab from "./components/permissions/DepartmentPermissionsTab.vue";
import { SCOPE_GROUPS, SCOPE_LABELS } from "../../../utils/TablePermissionScopes";
import { TABLE_API } from "../../../api/table";
import { mapState } from "vuex";
import { canEditTablePermissions } from "../../../utils/TablePermissionUtils";

export default Vue.extend({
    name: "TablePermissionsPage",
    components: {
        SupsisButton,
        UserPermissionsTab,
        DepartmentPermissionsTab,
    },
    data() {
        return {
            table: null,
            permissionMode: "public",
            permissionTargets: [],
            saving: false,
            activeTab: "users",
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data || [],
            departments: (state) => state.subject.subjects.data || [],
            currentUser: (state) => state.auth.user,
            adminUser: (state) => state.auth.adminUser,
        }),
        currentUserId() {
            return this.currentUser ? this.currentUser._id : null;
        },
        tableId() {
            return this.$route.params.id;
        },
        isCustomMode: {
            get() {
                return this.permissionMode === "custom";
            },
            set(value) {
                this.permissionMode = value ? "custom" : "public";
            },
        },
        scopeGroups() {
            return Object.values(SCOPE_GROUPS);
        },
        canEditPermissions() {
            return canEditTablePermissions(this.table, this.currentUser, this.adminUser);
        },
    },
    methods: {
        async loadTable() {
            try {
                this.table = await TABLE_API.getTableById(this.tableId);
                this.loadPermissionConfig();
            } catch (error) {
                console.error("Error loading table:", error);
                this.$message.error("Tablo yüklenirken hata oluştu");
                this.goBack();
            }
        },
        loadPermissionConfig() {
            const config = this.table.permissionConfig || {};
            this.permissionMode = config.mode || "public";
            this.permissionTargets = config.custom && Array.isArray(config.custom) ? [...config.custom] : [];
            console.log("Loaded permission config:", { mode: this.permissionMode, targets: this.permissionTargets });
        },
        onModeChange(isCustom) {
            if (!isCustom) {
                // Public'e geçerken onay al
                this.$confirm({
                    title: this.$t("tables.permissions.confirmPublicMode"),
                    content: this.$t("tables.permissions.confirmPublicModeDesc"),
                    okText: this.$t("common.yes"),
                    cancelText: this.$t("common.no"),
                    onOk: () => {
                        this.permissionMode = "public";
                        this.permissionTargets = [];
                    },
                    onCancel: () => {
                        this.isCustomMode = true;
                    },
                });
            } else {
                // Custom moda geçerken önemli kullanıcıları otomatik ekle
                this.addDefaultUsers();
            }
        },
        addDefaultUsers() {
            const { TABLE_PERMISSION_SCOPES } = require("../../../utils/TablePermissionScopes");
            const fullScopes = Object.values(TABLE_PERMISSION_SCOPES);
            const usersToAdd = [];

            // 1. Şu anki kullanıcı (ben)
            if (this.currentUser) {
                usersToAdd.push(this.currentUser._id);
            }

            // 2. Root kullanıcılar
            const rootUsers = this.users.filter((u) => u.role && u.role.isRoot);
            rootUsers.forEach((u) => {
                if (!usersToAdd.includes(u._id)) {
                    usersToAdd.push(u._id);
                }
            });

            // 3. Owner kullanıcı (isOwner: true)
            const ownerUser = this.users.find((u) => u.isOwner);
            if (ownerUser && !usersToAdd.includes(ownerUser._id)) {
                usersToAdd.push(ownerUser._id);
            }

            // 4. Tabloyu oluşturan kullanıcı (createdBy)
            if (this.table && this.table.createdBy) {
                const creatorId = typeof this.table.createdBy === "object" 
                    ? this.table.createdBy._id 
                    : this.table.createdBy;
                
                if (!usersToAdd.includes(creatorId)) {
                    usersToAdd.push(creatorId);
                }
            }

            // Kullanıcıları full yetkilerle ekle
            const newTargets = usersToAdd.map((userId) => ({
                target: "user",
                user: userId,
                department: null,
                scopes: fullScopes,
            }));

            this.permissionTargets = [...newTargets];
            
            console.log("Custom moda geçildi, otomatik eklenen kullanıcılar:", usersToAdd.length);
        },
        onActivateTarget({ target, targetType, targetId }) {
            const existingIndex = this.permissionTargets.findIndex((t) => {
                if (targetType === "user") {
                    return t.target === "user" && t.user && t.user.toString() === targetId.toString();
                } else {
                    return t.target === "department" && t.department && t.department.toString() === targetId.toString();
                }
            });

            if (existingIndex === -1) {
                // Yeni target ekle - Full yetkilerle
                const { TABLE_PERMISSION_SCOPES } = require("../../../utils/TablePermissionScopes");
                const fullScopes = Object.values(TABLE_PERMISSION_SCOPES);
                
                const newTarget = {
                    target: targetType,
                    user: targetType === "user" ? targetId : null,
                    department: targetType === "department" ? targetId : null,
                    scopes: fullScopes,
                };
                this.permissionTargets = [...this.permissionTargets, newTarget];
            } else {
                // Var olan target'ı kaldır
                this.permissionTargets = this.permissionTargets.filter((t, i) => i !== existingIndex);
            }
        },
        onToggleScope({ targetIndex, scope }) {
            const target = this.permissionTargets[targetIndex];
            if (!target) {
                console.error("Target bulunamadı:", targetIndex);
                return;
            }

            const currentScopes = target.scopes || [];
            
            if (currentScopes.includes(scope)) {
                // Scope'u kaldır - bağımlıları da kontrol et
                const dependentScopes = this.findDependentScopes(scope);
                const newScopes = currentScopes.filter(
                    (s) => s !== scope && !dependentScopes.includes(s)
                );
                this.$set(this.permissionTargets[targetIndex], "scopes", newScopes);
            } else {
                // Scope'u ekle - bağımlılıkları da ekle
                const dependencies = this.getScopeDependencies(scope);
                const newScopes = [...new Set([...currentScopes, scope, ...dependencies])];
                this.$set(this.permissionTargets[targetIndex], "scopes", newScopes);
            }
            
            // Force reactivity
            this.permissionTargets = [...this.permissionTargets];
        },
        getScopeDependencies(scope) {
            const { getScopeDependencies } = require("../../../utils/TablePermissionUtils");
            return getScopeDependencies(scope);
        },
        findDependentScopes(scope) {
            const { PERMISSION_DEPENDENCIES } = require("../../../utils/TablePermissionScopes");
            const dependents = [];
            Object.keys(PERMISSION_DEPENDENCIES).forEach((s) => {
                if (PERMISSION_DEPENDENCIES[s].includes(scope)) {
                    dependents.push(s);
                }
            });
            return dependents;
        },
        async onSave() {
            this.saving = true;
            try {
                const permissionConfig = {
                    mode: this.permissionMode,
                    custom: this.permissionMode === "custom" ? this.permissionTargets : [],
                };

                await TABLE_API.updateTable(this.table._id, { permissionConfig });
                
                this.$message.success(this.$t("tables.permissions.saveSuccess"));
                this.goBack();
            } catch (error) {
                console.error("Error saving permission config:", error);
                this.$message.error(this.$t("tables.permissions.saveError"));
            } finally {
                this.saving = false;
            }
        },
        onCancel() {
            this.goBack();
        },
        goBack() {
            this.$router.push(`/tables/t/${this.tableId}`);
        },
    },
    async mounted() {
        // Users ve departments yükle
        if (!this.users || this.users.length === 0) {
            await this.$store.dispatch("user/getUsers", { perPage: 1000, page: 1 });
        }
        
        if (!this.departments || this.departments.length === 0) {
            await this.$store.dispatch("subject/getSubjects", { perPage: 1000, page: 1 });
        }
        
        await this.loadTable();
        
        // Gizlilik ayarlarını düzenleme yetkisi kontrolü
        if (!this.canEditPermissions) {
            this.$message.error("Bu tablonun gizlilik ayarlarını düzenleme yetkiniz bulunmamaktadır");
            this.goBack();
        }
    },
});
</script>

<style scoped lang="scss">
.table-permissions-page {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--color-bg-default);

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        background: white;
        border-bottom: 1px solid var(--color-border-default);

        .header-left {
            display: flex;
            align-items: center;
            gap: 15px;

            .title-section {
                .page-title {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-size: 20px;
                    font-weight: 600;
                    margin: 0;

                    .material-icons {
                        font-size: 24px;
                    }
                }

                .page-subtitle {
                    margin: 5px 0 0 34px;
                    color: var(--color-text-subdued);
                    font-size: 14px;
                }
            }
        }

        .header-right {
            display: flex;
            gap: 10px;
        }
    }

    .page-content {
        flex: 1;
        overflow-y: auto;
        padding: 20px;

        .mode-switch-section {
            display: flex;
            flex-direction: column;
            gap: 10px;
            padding: 15px;
            background: white;
            border-radius: 8px;
            margin-bottom: 20px;
            border: 1px solid var(--color-border-default);

            .mode-label {
                display: flex;
                align-items: center;
                gap: 8px;
                font-weight: 600;
                font-size: 16px;

                .material-icons {
                    font-size: 20px;
                }
            }

            .mode-switch {
                display: flex;
                align-items: center;
            }

            .mode-description {
                font-size: 14px;
                color: var(--color-text-subdued);
            }
        }

        .custom-permissions {
            background: white;
            border-radius: 8px;
            border: 1px solid var(--color-border-default);
            overflow: hidden;
        }
    }
}
</style>

<style lang="scss">
.table-permissions-page {
    .ant-tabs-card {
        .ant-tabs-nav {
            margin: 0 !important;
        }

        .ant-tabs-content {
            padding: 20px;
        }
    }
}
</style>

